// #tag: ADMIN PAGE TEMPLATE COMPONENTS
// #purpose: Standardized UI scaffolding for admin console views, including headers, KPI strips, data tables, search bars, and sliding detail drawers.
import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { X } from 'lucide-react';
import { Illustration } from '../common/Illustration';

interface AdminPageLayoutProps {
  breadcrumb: string;
  title: string;
  description?: string;
  icon?: LucideIcon | React.ComponentType<{ size?: number | string }>;
  illustration?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

// #tag: ADMIN PAGE CONTAINER LAYOUT
// #purpose: Renders top breadcrumbs, page heading, header illustrations, action buttons, and responsive grid body.
export function AdminPageLayout({
  breadcrumb,
  title,
  description,
  icon: Icon,
  illustration,
  actions,
  children,
}: AdminPageLayoutProps) {
  return (
    <section className="wfx-admin-page">
      <header className="wfx-admin-page-head">
        <div className="wfx-admin-page-head-copy">
          <p className="wfx-admin-breadcrumb">{breadcrumb}</p>
          <h1>
            {Icon && <Icon size={22} aria-hidden="true" />}
            {title}
          </h1>
          {description && <p className="wfx-muted">{description}</p>}
        </div>
        {illustration && (
          <Illustration context={illustration} size="sm" className="wfx-admin-page-illustration" />
        )}
        {actions && <div className="wfx-admin-page-actions">{actions}</div>}
      </header>
      {children}
    </section>
  );
}

interface AdminKpiStripProps {
  items: Array<{ label: string; value: string | number }>;
}

// #tag: ADMIN KPI SUMMARY CARDS
// #purpose: Displays key numerical metrics and indicators in a responsive glassmorphic strip.
export function AdminKpiStrip({ items }: AdminKpiStripProps) {
  return (
    <div className="wfx-admin-kpi-grid wfx-admin-kpi-grid--compact">
      {items.map((item) => (
        <article key={item.label} className="wfx-admin-kpi glass-surface">
          <p className="wfx-admin-kpi-label">{item.label}</p>
          <strong className="wfx-admin-kpi-value">{item.value}</strong>
        </article>
      ))}
    </div>
  );
}

interface AdminTablePanelProps {
  title: string;
  toolbar?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

// #tag: ADMIN DATA TABLE PANEL
// #purpose: Wraps structured tabular data and search toolbars in a bordered card container.
export function AdminTablePanel({ title, toolbar, children, footer }: AdminTablePanelProps) {
  return (
    <article className="wfx-admin-panel glass-surface">
      <header className="wfx-admin-panel-head">
        <h2>{title}</h2>
        {toolbar}
      </header>
      <div className="wfx-admin-table-wrap">{children}</div>
      {footer}
    </article>
  );
}

export function AdminSearchInput({
  value,
  onChange,
  placeholder = 'Search…',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="wfx-admin-search wfx-admin-search--inline">
      <input
        type="search"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Search"
      />
    </label>
  );
}

export function AdminStatusBadge({ status }: { status: string }) {
  const normalized = status.toLowerCase();
  return (
    <span className={`wfx-admin-status-badge status-${normalized}`}>
      {status}
    </span>
  );
}

export function AdminEmptyState({ message }: { message: string }) {
  return <p className="wfx-muted wfx-admin-empty">{message}</p>;
}

interface AdminDetailCardProps {
  open: boolean;
  onClose: () => void;
  ariaLabel: string;
  breadcrumb: string;
  title: React.ReactNode;
  illustration?: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
}

// #tag: ADMIN DETAIL SLIDEOUT DRAWER
// #purpose: Provides an overlay drawer for inspecting and editing entity details without navigating away.
export function AdminDetailCard({
  open,
  onClose,
  ariaLabel,
  breadcrumb,
  title,
  illustration,
  subtitle,
  children,
}: AdminDetailCardProps) {
  if (!open) return null;

  return (
    <div className="wfx-admin-drawer-backdrop" role="presentation" onClick={onClose}>
      <aside
        className="wfx-admin-drawer glass-surface"
        role="dialog"
        aria-label={ariaLabel}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wfx-admin-drawer-head">
          <div className="wfx-admin-drawer-head-main">
            {illustration && (
              <Illustration
                context={illustration}
                size="md"
                className="wfx-admin-drawer-illustration"
              />
            )}
            <div className="wfx-admin-drawer-head-copy">
              <p className="wfx-admin-breadcrumb">{breadcrumb}</p>
              <h2>{title}</h2>
              {subtitle}
            </div>
          </div>
          <button type="button" className="wfx-admin-icon-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>
        <div className="wfx-admin-drawer-body">{children}</div>
      </aside>
    </div>
  );
}
